<template>
  <dt-collapsible
    element-type="li"
    max-width="100%"
    :open="item.link ? isOpen : true"
    class="dt-sidebar-item"
  >
    <template #anchor="{ attrs }">
      <dt-stack
        direction="row"
        class="d-ps-relative"
      >
        <dt-button
          :id="labelId"
          v-bind="attrs"
          :to="item.link || undefined"
          :active="isActiveLink(item.link)"
          importance="clear"
          kind="muted"
          label-class="d-jc-flex-start"
          icon-position="right"
          :tabindex="actionableTabIndex"
          :class="[
            'd-w100p d-fw-normal',
            {
              'd-headline--eyebrow d-fw-semibold d-fc-secondary d-bgc-transparent d-c-default': !item.link,
            },
          ]"
          @click="handleAnchorClick"
        >
          {{ item.text }}
          <template #icon="{ iconSize }">
            <dt-icon
              v-if="item.link"
              :name="isOpen ? 'chevron-down' : 'chevron-right'"
              :size="iconSize"
            />
          </template>
        </dt-button>
      </dt-stack>
    </template>
    <template #content>
      <dt-stack
        as="ul"
        :aria-labelledby="labelId"
        :class="{ 'd-pl8': nested }"
        gap="200"
      >
        <li
          v-for="(subItem, index) in subItems"
          :key="subItem.text"
        >
          <sidebar-item v-if="subItem.children" :item="subItem" nested />
          <dt-button
            v-else-if="!subItem.planned"
            :to="subItem.link"
            :active="isActiveLink(subItem.link)"
            importance="clear"
            kind="muted"
            label-class="d-jc-flex-start"
            :class="[
              'd-w100p d-fw-normal',
              {
                'd-mt2': (index === 0 && nested), // add margin top to first nested item
              },
            ]"
          >
            {{ subItem.text }}
          </dt-button>
          <div
            v-else
            class="d-btn d-w100p d-jc-flex-start d-fw-normal d-fc-disabled h:d-bgc-transparent d-c-default"
          >
            {{ subItem.text }}
            <dt-badge
              v-if="subItem.planned"
              class="d-fw-normal d-ml4"
            >
              Planned
            </dt-badge>
          </div>
        </li>
      </dt-stack>
    </template>
  </dt-collapsible>
</template>

<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useRoute } from 'vue-router';

const props = defineProps({
  isSinglePage: {
    type: Boolean,
    default: false,
  },
  item: {
    type: Object,
    default: () => {},
  },
  nested: {
    type: Boolean,
    default: false,
  },
});
const subItems = computed(() => {
  return props.item?.children || [];
});
const labelId = computed(() => {
  return `sidebar-label-${props.item?.text?.toLowerCase().replace(/\s+/g, '-')}`;
});
const actionableTabIndex = computed(() => {
  // Items without links are not actionable and should be removed from tab order
  return props.item.link ? undefined : -1;
});
const route = useRoute();
const hash = ref(route.hash);
const isOpen = ref(false);

watch(route, (newRoute) => {
  hash.value = newRoute.hash;
  isOpen.value = false;
}, { flush: 'pre', immediate: true, deep: true });

onMounted(() => {
  if (route.path === props.item.link) {
    isOpen.value = true;
  }
});

// isExactActive from router-link doesn't work with hashes,
// that's why we need to check for the hash if it's a single page.
// Now computed from route directly instead of router-link's scoped slot.
const isActiveLink = (link) => {
  if (!link) return false;
  const isExactActive = route.path === link;
  const active = props.isSinglePage ? hash.value === link : isExactActive;
  if (isExactActive) { isOpen.value = active; }
  return active;
};

function handleAnchorClick () {
  isOpen.value = true;
}
</script>

<style lang="less" scoped>
.dt-sidebar-item {
  width: var(--dt-size-100-percent);
}
</style>
